/**
 * MIT License
 * Copyright (c) 2023 Jamasp Team
 */
import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import HealthDataDisclosure from './HealthDataDisclosure';
import HealthDataSummary from './HealthDataSummary';

const STEPS = ['Email', 'Verify', 'Connect Google Health', 'Complete'];
const blue = '#0A66C2';
const gray = { text: '#94A6B8', border: '#D6E2EB', bg: '#FBFCFE', dark: '#131B20', mid: '#4C5967' };
const mono = { fontFamily: "'DM Mono', monospace" };

function Stepper({ activeStep }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 28 }}>
      {STEPS.map((label, i) => (
        <Fragment key={label}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
            <div style={{
              width: 26, height: 26, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 500, ...mono,
              background: i < activeStep ? blue : i === activeStep ? '#EFF6FF' : '#fff',
              color: i < activeStep ? '#fff' : i === activeStep ? blue : gray.text,
              border: `2px solid ${i <= activeStep ? blue : gray.border}`,
              transition: 'all 150ms',
            }}>
              {i < activeStep ? '✓' : i + 1}
            </div>
            <span style={{ fontSize: 9, ...mono, whiteSpace: 'nowrap', color: i === activeStep ? gray.dark : gray.text, fontWeight: i === activeStep ? 500 : 400 }}>
              {label}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <div style={{ flex: 1, height: 2, margin: '0 4px', marginBottom: 18, background: i < activeStep ? blue : gray.border, transition: 'background 150ms' }} />
          )}
        </Fragment>
      ))}
    </div>
  );
}
Stepper.propTypes = { activeStep: PropTypes.number.isRequired };

function PrimaryButton({ onClick, disabled, children }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding: '9px 22px', borderRadius: 8, border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
      background: disabled ? gray.border : `linear-gradient(to bottom, #0A66C2, #0959AA)`,
      color: disabled ? gray.text : '#fff',
      fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 500,
      boxShadow: disabled ? 'none' : 'inset 0 1px rgba(255,255,255,0.15), 0 0 0 1px #064079',
      transition: 'all 80ms',
    }}>
      {children}
    </button>
  );
}
PrimaryButton.propTypes = { onClick: PropTypes.func, disabled: PropTypes.bool, children: PropTypes.node };

function InputField({ label, type = 'text', value, onChange, error, helperText, placeholder }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ fontSize: 12, color: gray.mid, display: 'block', marginBottom: 5, fontWeight: 500 }}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          width: '100%', padding: '9px 12px', borderRadius: 8, fontSize: 14, boxSizing: 'border-box',
          border: `1px solid ${error ? '#FCA5A5' : focused ? blue : gray.border}`,
          fontFamily: "'DM Sans', sans-serif", background: '#fff', color: gray.dark, outline: 'none',
          transition: 'border-color 100ms',
        }}
      />
      {error && helperText && <p style={{ fontSize: 12, color: '#C0392B', margin: '4px 0 0' }}>{helperText}</p>}
    </div>
  );
}
InputField.propTypes = {
  label: PropTypes.string, type: PropTypes.string, value: PropTypes.string,
  onChange: PropTypes.func, error: PropTypes.bool, helperText: PropTypes.string, placeholder: PropTypes.string,
};

export default function EmailRegistrationStepper({
  email, activeStep, participantId, loading,
  onEmailChange, onStoreEmail, onInitiateFitbitAuth, onRenewRegistration, onVerifyEmail,
}) {
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');

  const validateEmail = (val) => setIsEmailValid(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val));

  const handleEmailChange = (e) => { onEmailChange(e.target.value); validateEmail(e.target.value); };
  const handleVerifyEmail = () => {
    onVerifyEmail(verificationCode)
      .then((r) => console.log('Verification result:', r))
      .catch((e) => console.error('Error verifying email:', e));
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
      <div style={{ width: 28, height: 28, borderRadius: '50%', border: `3px solid ${gray.border}`, borderTopColor: blue, animation: 'ja-spin 0.8s linear infinite' }} />
      <style>{`@keyframes ja-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  return (
    <div>
      <h2 style={{ fontSize: 17, fontWeight: 500, color: gray.dark, margin: '0 0 20px', textAlign: 'center' }}>
        Registration Wizard
      </h2>
      <Stepper activeStep={activeStep} />

      {activeStep === 0 && (
        <div>
          <InputField
            label="Email address"
            type="email"
            value={email}
            onChange={handleEmailChange}
            placeholder="you@example.com"
            error={email !== '' && !isEmailValid}
            helperText="Please enter a valid email address"
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <PrimaryButton onClick={onStoreEmail} disabled={!isEmailValid || loading}>Continue →</PrimaryButton>
          </div>
        </div>
      )}

      {activeStep === 1 && (
        <div>
          <p style={{ fontSize: 14, color: gray.mid, lineHeight: 1.65, marginBottom: 16 }}>
            A verification code has been sent to your email. Enter it below to continue.
          </p>
          <InputField
            label="Verification code"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            placeholder="Enter code"
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <PrimaryButton onClick={handleVerifyEmail} disabled={verificationCode.length < 4 || loading}>Verify email →</PrimaryButton>
          </div>
        </div>
      )}

      {activeStep === 2 && (
        <div>
          <p style={{ fontSize: 14, color: gray.mid, lineHeight: 1.65, marginBottom: 16 }}>
            Connect your Google Health account to authorize access to your Fitbit data for this study. When Google asks which data to share, tick <strong>Select all</strong>.
          </p>
          <HealthDataDisclosure />
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <PrimaryButton onClick={onInitiateFitbitAuth} disabled={loading}>Connect Google Health →</PrimaryButton>
          </div>
        </div>
      )}

      {activeStep === 3 && (
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 52, height: 52, borderRadius: '50%', background: '#ECFDF5',
            border: '2px solid #6EE7B7', display: 'flex', alignItems: 'center',
            justifyContent: 'center', margin: '0 auto 16px', fontSize: 22,
          }}>✓</div>
          <h3 style={{ fontSize: 17, fontWeight: 500, color: gray.dark, margin: '0 0 8px' }}>Registration complete!</h3>
          <p style={{ fontSize: 14, color: gray.mid, marginBottom: 16 }}>Your participant ID is:</p>
          <div style={{
            background: gray.bg, border: `1px solid ${gray.border}`, borderRadius: 8,
            padding: '14px 20px', marginBottom: 20, ...mono, fontSize: 16,
            fontWeight: 500, color: blue, wordBreak: 'break-all',
          }}>
            {participantId}
          </div>
          <p style={{ fontSize: 13, color: gray.mid, marginBottom: 20 }}>Please save this ID for your records.</p>
          <button onClick={onRenewRegistration} style={{
            padding: '8px 18px', borderRadius: 8, border: `1px solid ${gray.border}`,
            background: 'transparent', color: gray.mid, fontFamily: "'DM Sans', sans-serif",
            fontSize: 13, cursor: 'pointer',
          }}>Start over</button>
          <HealthDataSummary />
        </div>
      )}
    </div>
  );
}

EmailRegistrationStepper.propTypes = {
  email: PropTypes.string.isRequired,
  activeStep: PropTypes.number.isRequired,
  participantId: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  onEmailChange: PropTypes.func.isRequired,
  onStoreEmail: PropTypes.func.isRequired,
  onInitiateFitbitAuth: PropTypes.func.isRequired,
  onRenewRegistration: PropTypes.func.isRequired,
  onVerifyEmail: PropTypes.func.isRequired,
};
